import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';

import { ItemType } from '../generated-mesh/graphql';

type DownvoteState = {
  [key in ItemType]: boolean;
};

interface DownvoteContextValue {
  // Check if any item of a type has been downvoted
  hasDownvoted: (itemType: ItemType) => boolean;

  // Mark that an item of this type has been downvoted
  markAsDownvoted: (itemType: ItemType) => void;
}

const DownvoteContext = createContext<DownvoteContextValue | undefined>(
  undefined
);

export const DownvoteProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [downvoteState, setDownvoteState] = useState<DownvoteState>({
    [ItemType.JOB]: false,
    [ItemType.COURSE]: false,
    [ItemType.CAREER]: false,
  });

  const hasDownvoted = useCallback(
    (itemType: ItemType): boolean => {
      return downvoteState[itemType] || false;
    },
    [downvoteState]
  );

  const markAsDownvoted = useCallback((itemType: ItemType) => {
    setDownvoteState((prev) => ({
      ...prev,
      [itemType]: true,
    }));
  }, []);

  const value: DownvoteContextValue = useMemo(
    () => ({
      hasDownvoted,
      markAsDownvoted,
    }),
    [hasDownvoted, markAsDownvoted]
  );

  return (
    <DownvoteContext.Provider value={value}>
      {children}
    </DownvoteContext.Provider>
  );
};

export const useDownvote = (): DownvoteContextValue => {
  const context = useContext(DownvoteContext);
  if (!context) {
    throw new Error('useDownvote must be used within a DownvoteProvider');
  }
  return context;
};
